import { storeToRefs } from 'pinia';
import { computed, ComputedRef } from 'vue';
import { useSegmentListStore } from '@/store/dictionary/segment-list.store';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import { TagListParamsRequest } from '@/models/dictionary/tag-list.model';
import { useMultiWhitelabelListStore } from '@/store/multi-whitelabel-slug-list/multi-whitelabel-slug-list.store';
import { useTagExternalIdListStore } from '@/store/tag-external/tag-external-list.store';
import { useSegmentExternalIdListStore } from '@/store/segment-external/segment-external-list.store';
import { ExternalIdListItem, ExternalIdListItemIdList } from '@/models/external-id-list/external-id-list-item.model';
import { showErrorNotification } from '@/utils/show-notification.util';
import { documentListFilterFormRef, useDocumentListFilterStore } from '@/store/documents/document-filter-list.store';
import { useEnvStore } from '@/store/env.store';

type DocumentListFilterComposables = {
  whiteLabelIdList: ComputedRef<string[]>;
  updateLoadedDictionaries: () => void;
  getExternalFilterIdList: () => Promise<ExternalFilterIdList>;
  getExternalFilterIdMap: () => Promise<ExternalFilterIdMap>;
  submitAndValidateForm: () => Promise<boolean>;
}

type ExternalFilterIdList = {
  tagExternalIdList: ExternalIdListItem[];
  segmentExternalIdList: ExternalIdListItem[];
};

type ExternalFilterIdMap = {
  tagIdMap: Map<string, string[]>;
  segmentIdMap: Map<string, string[]>;
};

export const useDocumentListFilter = (): DocumentListFilterComposables => {
  const tagListStore = useTagListStore();
  const { isTagListNotEmpty } = storeToRefs(tagListStore);
  const { getTagList } = tagListStore;

  const segmentListStore = useSegmentListStore();
  const { isSegmentListNotEmpty } = storeToRefs(segmentListStore);
  const { getSegmentList } = segmentListStore;

  const { multiWhitelabelIdList } = storeToRefs(useMultiWhitelabelListStore());
  const { getTagExternalList } = useTagExternalIdListStore();
  const { getSegmentExternalList } = useSegmentExternalIdListStore();
  const { WHITELABEL_ID } = storeToRefs(useEnvStore());

  const {
    filterTagIdList,
    filterSegmentIdList,
    isFilterSegmentIdListNotEmpty,
    isFilterTagIdListNotEmpty,
  } = storeToRefs(useDocumentListFilterStore());

  const whiteLabelIdList = computed<string[]>(() => [...multiWhitelabelIdList.value, WHITELABEL_ID.value]);

  const updateLoadedDictionaries = (): void => {
    const filterList = { wlIdList: whiteLabelIdList.value };

    if (isSegmentListNotEmpty.value) {
      getSegmentList({
        filterList: { ...filterList, isPublished: true },
        isForceUpdate: true,
      });
    }
    if (isTagListNotEmpty.value) {
      getTagList({
        object: TagListParamsRequest.PLAYER,
        filterList,
        isForceUpdate: true,
      });
    }
  };

  const getExternalFilterIdList = async (): Promise<ExternalFilterIdList> => {
    const [tagExternalIdList, segmentExternalIdList] = await Promise.all([
      isFilterTagIdListNotEmpty.value ? getTagExternalList({ tagId: filterTagIdList.value }) : [],
      isFilterSegmentIdListNotEmpty.value ? getSegmentExternalList({ segmentId: filterSegmentIdList.value }) : [],
    ]);

    return {
      tagExternalIdList,
      segmentExternalIdList,
    };
  };

  const createExternalIdMap = (externalIdList: ExternalIdListItem[]): Map<string, string[]> => {
    const externalIdMap = new Map<string, string[]>();

    externalIdList
      .flatMap(({ externalIds }: ExternalIdListItem) => externalIds)
      .forEach(({ wlSlug, id }: ExternalIdListItemIdList) => {
        const idList = externalIdMap.get(wlSlug) ?? [];

        idList.push(id);

        externalIdMap.set(wlSlug, idList);
      });

    return externalIdMap;
  };

  const getExternalFilterIdMap = async (): Promise<ExternalFilterIdMap> => {
    const { tagExternalIdList, segmentExternalIdList } = await getExternalFilterIdList();

    return {
      tagIdMap: createExternalIdMap(tagExternalIdList),
      segmentIdMap: createExternalIdMap(segmentExternalIdList),
    };
  };

  const submitAndValidateForm = async (): Promise<boolean> => {
    if (!documentListFilterFormRef.value) {
      showErrorNotification(new Error('Form is not ready'));

      return false;
    }

    try {
      await documentListFilterFormRef.value.validate();

      return true;
    } catch {
      showErrorNotification(
        new Error('Please properly fill all required fields.'),
      );

      return false;
    }
  };

  return {
    whiteLabelIdList,
    updateLoadedDictionaries,
    getExternalFilterIdList,
    getExternalFilterIdMap,
    submitAndValidateForm,
  };
};
